import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * In-app confirmation modal that replaces window.confirm(). Provides a
 * promise-based API:
 *
 *   const confirm = useConfirm();
 *   if (await confirm("¿Eliminar foto?")) { ... }
 *
 * Or with options:
 *   await confirm({ title: "Eliminar archivo", description: "...", danger: true, confirmText: "Eliminar" })
 */

const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, opts: null });
  const resolverRef = useRef(null);

  const confirm = useCallback((arg) => {
    const opts = typeof arg === "string" ? { description: arg } : arg || {};
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, opts });
    });
  }, []);

  const close = (value) => {
    setState((s) => ({ ...s, open: false }));
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(value);
  };

  const o = state.opts || {};
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(v) => { if (!v) close(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{o.title || "¿Confirmar acción?"}</AlertDialogTitle>
            {o.description ? (
              <AlertDialogDescription>{o.description}</AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => close(false)} data-testid="confirm-cancel">
              {o.cancelText || "Cancelar"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => close(true)}
              className={o.danger ? "bg-red-600 hover:bg-red-700 text-white" : ""}
              data-testid="confirm-accept"
            >
              {o.confirmText || (o.danger ? "Eliminar" : "Aceptar")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    // Fallback so callers don't crash if provider missing
    return async (arg) => window.confirm(typeof arg === "string" ? arg : (arg?.description || arg?.title || "¿Confirmar?"));
  }
  return ctx;
}
